import { useState, useEffect, useRef } from 'react';

const API = import.meta.env.VITE_API_URL || '/api';

interface Category {
  id: string;
  name: string;
}

interface Product {
  id: string;
  name: string;
  sku?: string;
  barcode?: string;
  description?: string;
  brand?: string;
  salePrice?: number | string;
  costPrice?: number | string;
  unit?: string;
  purchaseUnit?: string;
  imagePath?: string | null;
  categoryId?: string;
  minQuantity?: number | string;
  isAvailableForSale?: boolean;
  suppliers?: { id: string; name: string; costPrice?: string | null; isDefault?: boolean }[];
  presentations?: { side: 'sale' | 'purchase'; name: string; factor: string | number; price?: string | number | null }[];
}

/**
 * A product found in the shared catalog that is NOT in this comercio's list
 * (active or inactive). The catalog is read-only: adding one copies it here.
 */
interface CatalogHit {
  id: string;
  gtin: string | null;
  sku: string | null;
  name: string;
  description: string | null;
  saleUnit: string | null;
  purchaseUnit: string | null;
  brand: string | null;
  categoryName: string | null;
  matchType: 'barcode' | 'sku' | 'name';
}

interface StockLevel {
  productId: string;
  quantity: string;
  minQuantity: string;
}

export function Products() {
  const [products, setProducts] = useState<Product[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [suppliers, setSuppliers] = useState<{ id: string; name: string }[]>([]);
  const [supplierPricing, setSupplierPricing] = useState<{ supplierId: string; costPrice: string; isDefault: boolean }[]>([]);
  const [stockMap, setStockMap] = useState<Record<string, { quantity: string; minQuantity: string }>>({});
  const [search, setSearch] = useState('');
  // Catálogo general: lo que se busca acá y NO está en la lista del comercio.
  const [catalogHits, setCatalogHits] = useState<CatalogHit[]>([]);
  const [catalogVertical, setCatalogVertical] = useState<{ name: string } | null>(null);
  const [catalogLoading, setCatalogLoading] = useState(false);
  const [catalogAddId, setCatalogAddId] = useState<string | null>(null);
  const [catalogForm, setCatalogForm] = useState({
    name: '', sku: '', categoryId: '', salePrice: '', costPrice: '',
  });
  const [catalogBusy, setCatalogBusy] = useState(false);
  const [showInactive, setShowInactive] = useState(false);
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [showForm, setShowForm] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);
  const [adjustStockId, setAdjustStockId] = useState<string | null>(null);
  // Actualización masiva de precios
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkMode, setBulkMode] = useState<'percent' | 'amount'>('percent');
  const [bulkValue, setBulkValue] = useState('');
  const [bulkScope, setBulkScope] = useState<'all' | 'suppliers'>('all');
  const [bulkSupplierIds, setBulkSupplierIds] = useState<string[]>([]);
  const [bulkBusy, setBulkBusy] = useState(false);
  // Gestión de categorías
  const [catModalOpen, setCatModalOpen] = useState(false);
  const [newCat, setNewCat] = useState('');
  const [renamingId, setRenamingId] = useState<string | null>(null);
  const [renameVal, setRenameVal] = useState('');
  const [catError, setCatError] = useState('');
  const [stockForm, setStockForm] = useState({ type: 'in', quantity: '', reason: '', newMin: '' });
  const [form, setForm] = useState({
    name: '',
    sku: '',
    barcode: '',
    description: '',
    brand: '',
    salePrice: '',
    costPrice: '',
    unit: 'unit',
    purchaseUnit: 'unit',
    categoryId: '',
    minQuantity: '',
    isAvailableForSale: true,
    fractionalSale: false,
    packSize: '',
    cajaSize: '',
    presentations: [] as { side: 'sale' | 'purchase'; name: string; factor: string; price: string }[],
  });
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [saveMessage, setSaveMessage] = useState<string | null>(null);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [suggestions, setSuggestions] = useState<Product[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const formRef = useRef<HTMLFormElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  const token = localStorage.getItem('accessToken');
  const headers = {
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json',
    'x-tenant-slug': localStorage.getItem('tenantSlug') || '',
  };

  // Validation helpers
  const validateForm = (values: typeof form): { isValid: boolean; errors: Record<string, string> } => {
    const errors: Record<string, string> = {};
    if (!values.name.trim()) errors.name = 'El nombre es requerido';
    if (values.salePrice === '' || parseFloat(values.salePrice) <= 0) errors.salePrice = 'Precio de venta requerido y debe ser mayor a 0';
    if (values.costPrice !== '' && parseFloat(values.costPrice) < 0) errors.costPrice = 'El costo no puede ser negativo';
    return {
      isValid: Object.keys(errors).length === 0,
      errors,
    };
  };

  // Load data with debounced search
  useEffect(() => {
    const handler = setTimeout(() => loadData(), 300);
    return () => clearTimeout(handler);
  }, [search, showInactive, categoryFilter]);

  // Catálogo general: al buscar, se busca también ahí. El backend devuelve SOLO
  // lo que NO está en la lista del comercio (activos o no), así que nada de lo
  // que ya tenés aparece duplicado acá abajo.
  const catalogTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    const term = search.trim();
    if (term.length < 2) {
      setCatalogHits([]);
      return;
    }
    if (catalogTimer.current) clearTimeout(catalogTimer.current);
    catalogTimer.current = setTimeout(async () => {
      setCatalogLoading(true);
      try {
        const res = await fetch(`${API}/catalog/search?q=${encodeURIComponent(term)}&limit=10`, { headers });
        if (!res.ok) {
          setCatalogHits([]);
          return;
        }
        const d = await res.json().catch(() => ({}));
        setCatalogVertical(d.data?.vertical ?? null);
        setCatalogHits(d.data?.hits ?? []);
      } catch {
        setCatalogHits([]);
      } finally {
        setCatalogLoading(false);
      }
    }, 350);
    return () => {
      if (catalogTimer.current) clearTimeout(catalogTimer.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  function openCatalogAdd(hit: CatalogHit) {
    setCatalogAddId(catalogAddId === hit.id ? null : hit.id);
    setCatalogForm({
      name: hit.name,
      sku: hit.sku ?? '',
      categoryId: '',
      salePrice: '',
      costPrice: '',
    });
  }

  async function addFromCatalog(hit: CatalogHit) {
    setCatalogBusy(true);
    setError(null);
    try {
      const res = await fetch(`${API}/catalog/copy`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          catalogItemId: hit.id,
          salePrice: catalogForm.salePrice,
          costPrice: catalogForm.costPrice || null,
          name: catalogForm.name || null,
          sku: catalogForm.sku || null,
          categoryId: catalogForm.categoryId || null,
        }),
      });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(d.error || 'No se pudo agregar el producto');
      setCatalogAddId(null);
      setCatalogHits((prev) => prev.filter((h) => h.id !== hit.id));
      const cat = d.categoryCreated
        ? ` Se creó la categoría "${d.categoryMatched}" en tu comercio.`
        : d.categoryMatched
          ? ` Categoría: ${d.categoryMatched}.`
          : '';
      setSaveMessage(`"${hit.name}" se agregó a tus productos.${cat}`);
      await loadData();
    } catch (e: any) {
      setError(e.message || 'No se pudo agregar el producto');
    } finally {
      setCatalogBusy(false);
    }
  }

  async function loadData() {
    setLoading(true);
    setError(null);
    try {
      const prodUrl = `${API}/products?q=${search}&includeInactive=${showInactive ? 'true' : 'false'}${categoryFilter && categoryFilter !== 'all' ? `&categoryId=${categoryFilter}` : ''}`;
      const [prodRes, catRes, stockRes, supRes] = await Promise.all([
        fetch(prodUrl, { headers }),
        fetch(`${API}/categories`, { headers }),
        fetch(`${API}/stock/levels`, { headers }),
        fetch(`${API}/suppliers`, { headers }),
      ]);
      let fetchedProducts: Product[] = [];
      if (prodRes.ok) { const d = await prodRes.json(); fetchedProducts = d.data || []; setProducts(fetchedProducts); }
      else { const err = await prodRes.json(); throw new Error(err.error || 'Error al cargar productos'); }
      if (catRes.ok) { const d = await catRes.json(); setCategories(d.data || []); }
      if (supRes.ok) { const d = await supRes.json(); setSuppliers(d.data || []); }
      if (stockRes.ok) {
        const d = await stockRes.json();
        const map: Record<string, { quantity: string; minQuantity: string }> = {};
        for (const s of (d.data || [])) map[s.productId] = { quantity: s.quantity, minQuantity: s.minQuantity };
        setStockMap(map);
      } else {
        const err = await stockRes.json();
        throw new Error(err.error || 'Error al cargar niveles de stock');
      }
      // Update suggestions for autocomplete
      if (search.length >= 2) {
        setSuggestions(fetchedProducts.filter(p =>
          p.name.toLowerCase().includes(search.toLowerCase()) ||
          (p.sku && p.sku.toLowerCase().includes(search.toLowerCase()))
        ));
        setShowSuggestions(true);
      } else {
        setShowSuggestions(false);
      }
    } catch (err: any) {
      setError(err.message ?? 'Error inesperado al cargar datos');
    } finally {
      setLoading(false);
    }
  }

  function resetForm() {
    setForm({
      name: '',
      sku: '',
      barcode: '',
      description: '',
      brand: '',
      salePrice: '',
      costPrice: '',
      unit: 'unit',
      purchaseUnit: 'unit',
categoryId: '',
    minQuantity: '',
    isAvailableForSale: true,
    fractionalSale: false,
    packSize: '',
    cajaSize: '',
    presentations: [],
  });
    setEditingId(null);
    setShowAdvanced(false);
    setImageFile(null);
    setImagePreview(null);
    setFormErrors({});
    setSaveMessage(null);
    setSupplierPricing([]);
  }

  function startEdit(p: Product) {
    setEditingId(p.id);
    setForm({
      name: p.name || '',
      sku: p.sku || '',
      barcode: p.barcode || '',
      description: p.description || '',
      brand: p.brand || '',
      salePrice: p.salePrice?.toString() || '',
      costPrice: p.costPrice?.toString() || '',
      unit: p.unit || 'unit',
      purchaseUnit: p.purchaseUnit || 'unit',
      categoryId: p.categoryId || '',
      minQuantity: p.minQuantity?.toString() || '',
      isAvailableForSale: p.isAvailableForSale ?? true,
      fractionalSale: (p.presentations || []).some(x => x.side === 'sale'),
      packSize: (p.presentations || []).find(x => x.side === 'purchase' && x.name.toLowerCase() === 'pack')?.factor?.toString() || '',
      cajaSize: (p.presentations || []).find(x => x.side === 'purchase' && (x.name.toLowerCase() === 'caja' || x.name.toLowerCase() === 'box'))?.factor?.toString() || '',
      presentations: (p.presentations || []).map(x => ({
        side: x.side,
        name: x.name,
        factor: String(x.factor),
        price: x.price != null ? String(x.price) : '',
      })),
    });
    setSupplierPricing((p.suppliers || []).map(s => ({
      supplierId: s.id,
      costPrice: s.costPrice ?? '',
      isDefault: s.isDefault ?? false,
    })));
    setShowForm(true);
    setShowAdvanced(true); // show advanced fields when editing
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSubmitting(true);
    const { isValid, errors } = validateForm(form);
    setFormErrors(errors);
    if (!isValid) {
      setSubmitting(false);
      return;
    }
    try {
      const body: Record<string, string | number | boolean | string[] | { supplierId: string; costPrice: string | number | null; isDefault: boolean }[] | { side: string; name: string; factor: string | number; price?: number | null | string }[] | undefined> = {
        name: form.name,
        sku: form.sku || undefined,
        barcode: form.barcode || undefined,
        description: form.description || undefined,
        brand: form.brand || undefined,
        salePrice: parseFloat(form.salePrice),
        costPrice: form.costPrice ? parseFloat(form.costPrice) : undefined,
        unit: form.unit,
        purchaseUnit: form.purchaseUnit,
        categoryId: form.categoryId || undefined,
        isAvailableForSale: form.isAvailableForSale,
        presentations: (() => {
          const list: { side: 'sale' | 'purchase'; name: string; factor: string; price?: number | null }[] = [];
          // Pack / caja: factor definido en el campo de la unidad (venta y/o compra)
          if (form.unit === 'pack' && form.packSize) list.push({ side: 'sale', name: 'pack', factor: form.packSize, price: null });
          if (form.unit === 'box' && form.cajaSize) list.push({ side: 'sale', name: 'caja', factor: form.cajaSize, price: null });
          if (form.purchaseUnit === 'pack' && form.packSize) list.push({ side: 'purchase', name: 'pack', factor: form.packSize });
          if (form.purchaseUnit === 'box' && form.cajaSize) list.push({ side: 'purchase', name: 'caja', factor: form.cajaSize });
          // Venta fraccionada: presentaciones tildadas con precio propio
          if (form.fractionalSale) {
            for (const x of form.presentations) {
              if (!x.name.trim()) continue;
              const exists = list.some(e => e.side === x.side && e.name.toLowerCase() === x.name.trim().toLowerCase());
              if (!exists) {
                list.push({
                  side: x.side,
                  name: x.name.trim(),
                  factor: x.factor,
                  price: x.side === 'sale' ? (x.price ? parseFloat(x.price) : null) : undefined,
                });
              }
            }
          }
          return list;
        })(),
        supplierPricing: supplierPricing.length
          ? supplierPricing.map(r => ({
              supplierId: r.supplierId,
              costPrice: r.costPrice === '' ? null : r.costPrice,
              isDefault: r.isDefault,
            }))
          : undefined,
      };
      let res;
      if (editingId) {
        res = await fetch(`${API}/products/${editingId}`, {
          method: 'PUT',
          headers,
          body: JSON.stringify(body),
        });
      } else {
        res = await fetch(`${API}/products`, {
          method: 'POST',
          headers,
          body: JSON.stringify(body),
        });
      }
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.error || 'Error al guardar');
      }
      const saved = (await res.json()) as { data?: { id: string } };
      // Si elegí imagen, subirla al producto recién guardado (convierte a WebP)
      if (imageFile && saved.data) {
        const fd = new FormData();
        fd.append('file', imageFile);
        const up = await fetch(`${API}/products/upload-image?productId=${saved.data.id}`, {
          method: 'POST',
          headers: { Authorization: headers.Authorization, 'x-tenant-slug': headers['x-tenant-slug'] },
          body: fd,
        });
        if (!up.ok) {
          const ude = await up.json().catch(() => ({}));
          setError(`Producto guardado, pero no se pudo subir la imagen: ${ude.error || 'error'}`);
        }
      }
      setSaveMessage('Guardado con éxito');
      setTimeout(() => setSaveMessage(null), 3000);
      resetForm();
      loadData();
    } catch (err: any) {
      setError(err.message ?? 'Error inesperado');
    } finally {
      setSubmitting(false);
    }
  }

  async function handleDelete(id: string) {
    try {
      // DELETE sin body: NO mandar Content-Type (fastify 400 si va JSON vacío)
      const delHeaders = { ...headers } as Record<string, string>;
      delete delHeaders['Content-Type'];
      const res = await fetch(`${API}/products/${id}`, { method: 'DELETE', headers: delHeaders });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.error || 'Error al eliminar');
      }
      setConfirmDelete(null);
      loadData();
    } catch (err: any) {
      setError(err.message ?? 'Error inesperado');
    }
  }

  async function handleToggleActive(p: Product) {
    try {
      const res = await fetch(`${API}/products/${p.id}`, {
        method: 'PUT',
        headers,
        body: JSON.stringify({ isAvailableForSale: !(p.isAvailableForSale ?? true) }),
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.error || 'Error al cambiar estado');
      }
      loadData();
    } catch (err: any) {
      setError(err.message ?? 'Error inesperado');
    }
  }

  function openAdjustStock(productId: string) {
    setAdjustStockId(productId);
    setStockForm({ type: 'in', quantity: '', reason: '', newMin: '' });
  }

  async function handleStockAdjust(e: React.FormEvent) {
    e.preventDefault();
    if (!adjustStockId || !stockForm.quantity) return;
    try {
      const body: Record<string, string | number | boolean | undefined> = {
        productId: adjustStockId,
        type: stockForm.type,
        quantity: Number(stockForm.quantity),
        reason: stockForm.reason || undefined
      };
      const res = await fetch(`${API}/stock/adjust`, { method: 'POST', headers, body: JSON.stringify(body) });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.error || 'Error al ajustar stock');
      }
      setAdjustStockId(null);
      setStockForm({ type: 'in', quantity: '', reason: '', newMin: '' });
      loadData();
    } catch (err: any) {
      setError(err.message ?? 'Error inesperado');
    }
  }

  // Handle keyboard shortcuts: Ctrl+Enter to submit when form is focused
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.ctrlKey && e.key === 'Enter' && showForm && !submitting) {
        e.preventDefault();
        if (formRef.current) {
          formRef.current.requestSubmit();
        }
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [showForm, submitting]);

  // Close suggestions when clicking outside
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(e.target as Node)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // ===== Actualización masiva de precios =====
  const fmt$ = (n: number) => `$${n.toFixed(0)}`;

  // ===== Gestión de categorías =====
  async function createCat() {
    const name = newCat.trim();
    if (!name) return;
    setCatError('');
    try {
      const res = await fetch(`${API}/categories`, { method: 'POST', headers, body: JSON.stringify({ name }) });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(d.error || 'Error al crear categoría');
      setNewCat('');
      await loadData();
    } catch (e: any) {
      setCatError(e.message || 'Error');
    }
  }

  async function renameCat(id: string) {
    const name = renameVal.trim();
    if (!name) return;
    setCatError('');
    try {
      const res = await fetch(`${API}/categories/${id}`, { method: 'PUT', headers, body: JSON.stringify({ name }) });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(d.error || 'Error al renombrar');
      setRenamingId(null);
      await loadData();
    } catch (e: any) {
      setCatError(e.message || 'Error');
    }
  }

  async function deleteCat(id: string, name: string) {
    if (!window.confirm(`¿Eliminar la categoría "${name}"? Solo se borra si no tiene productos.`)) return;
    setCatError('');
    try {
      const res = await fetch(`${API}/categories/${id}`, {
        method: 'DELETE',
        headers: { Authorization: headers.Authorization, 'x-tenant-slug': headers['x-tenant-slug'] },
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        throw new Error(d.error || 'Error al eliminar');
      }
      await loadData();
    } catch (e: any) {
      setCatError(e.message || 'Error');
    }
  }

  function bulkTargetCount() {
    return products.filter((p) => {
      const qty = Number(stockMap[p.id]?.quantity || 0);
      if (!(p.isAvailableForSale ?? true) || qty <= 0) return false;
      if (bulkScope === 'suppliers') {
        const supIds = (p.suppliers || []).map((s) => s.id);
        return bulkSupplierIds.some((id) => supIds.includes(id));
      }
      return true;
    });
  }

  async function applyBulk() {
    const value = parseFloat(bulkValue);
    if (!value || Number.isNaN(value)) { setError('Ingresá un valor válido (ej: 10 o -10)'); return; }
    if (bulkScope === 'suppliers' && bulkSupplierIds.length === 0) { setError('Seleccioná al menos un proveedor'); return; }
    setBulkBusy(true);
    setError(null);
    try {
      const res = await fetch(`${API}/products/bulk-price-update`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          mode: bulkMode,
          value,
          scope: bulkScope,
          supplierIds: bulkScope === 'suppliers' ? bulkSupplierIds : undefined,
        }),
      });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(d.error || 'Error al actualizar precios');
      setBulkOpen(false);
      await loadData();
      window.alert(`✓ Precios actualizados en ${d.data?.updated ?? 0} producto(s)`);
    } catch (e: any) {
      setError(e.message || 'Error al actualizar precios');
    } finally {
      setBulkBusy(false);
    }
  }

  const bulkTargets = bulkOpen ? bulkTargetCount() : [];
  const bulkValueNum = parseFloat(bulkValue) || 0;
  const bulkSample = bulkTargets[0] ? Number(bulkTargets[0].salePrice) : null;
  const bulkSampleNew = bulkSample != null && bulkValueNum
    ? (bulkMode === 'percent' ? Math.round(bulkSample * (1 + bulkValueNum / 100) * 100) / 100 : bulkSample + bulkValueNum)
    : null;

  // Muestra el stock en la UNIDAD DE COMPRA (más clara para administrar el
  // stock: ej: "3 maple" en vez de "90 u."). En la lista solo se indican las
  // unidades de compra ENTERAS (sin decimales ni fracciones restantes): si
  // hay 14 latas y el pack es de 6, se muestra "2 packs" (no "2,33").
  //
  // Normaliza la unidad para comparar alias (caja == box) y resuelve
  // conversiones de peso/volumen (kg→g, L→ml) como en la recepción de OC.
  function normUnitForCompare(u: string): string {
    const s = (u || '').trim().toLowerCase();
    if (s === 'box' || s === 'caja' || s === 'cajas') return 'box';
    if (s === 'pack' || s === 'packs') return 'pack';
    if (s === 'lt' || s === 'l' || s === 'litro' || s === 'litros') return 'lt';
    return s;
  }

  function purchaseUnitLabel(u: string): string {
    const s = (u || '').trim().toLowerCase();
    if (s === 'box' || s === 'caja' || s === 'cajas') return 'cajas';
    if (s === 'pack' || s === 'packs') return 'packs';
    if (s === 'kg') return 'kg';
    if (s === 'g' || s === 'gr' || s === 'gramo' || s === 'gramos') return 'g';
    if (s === 'lt' || s === 'l' || s === 'litro' || s === 'litros') return 'litros';
    if (s === 'ml' || s === 'mililitro' || s === 'mililitros') return 'mililitros';
    if (s === 'm' || s === 'metro' || s === 'metros') return 'metros';
    if (s === 'unit') return 'u.';
    return u || 'u.';
  }

  // Factor que convierte el stock (en unidad de venta) a la unidad de compra.
  function purchaseFactor(p: Product): { factor: number; label: string; saleUnit: string } {
    const saleUnit = (p.unit && p.unit !== 'unit') ? p.unit : 'u.';
    const pres = (p.presentations || []).find(
      x => x.side === 'purchase' && normUnitForCompare(x.name) === normUnitForCompare(p.purchaseUnit || 'unit'),
    );
    if (pres && Number(pres.factor) > 1) {
      return { factor: Number(pres.factor), label: purchaseUnitLabel(pres.name), saleUnit };
    }
    // Conversión de peso/volumen: el stock está en g/ml y la compra en kg/L
    const buyU = normUnitForCompare(p.purchaseUnit || 'unit');
    const saleU = normUnitForCompare(p.unit || 'unit');
    if (buyU === 'kg' && saleU === 'g') return { factor: 1000, label: 'kg', saleUnit };
    if (buyU === 'lt' && saleU === 'ml') return { factor: 1000, label: 'litros', saleUnit };
    return { factor: 1, label: purchaseUnitLabel(p.purchaseUnit || ''), saleUnit };
  }

  function stockInPurchaseUnit(p: Product): { value: string; unit: string } {
    const qty = Number(stockMap[p.id]?.quantity || 0);
    const { factor, label } = purchaseFactor(p);
    const divisor = factor > 0 ? factor : 1;
    const whole = Math.floor(qty / divisor);
    return { value: String(whole), unit: label };
  }

  // Desglose EXACTO del stock para el detalle del producto: unidades de compra
  // enteras + resto en unidades de venta. Ej: 14 latas con pack=6 →
  // "2 packs y 2 unidades".
  function stockBreakdown(p: Product): string {
    const qty = Number(stockMap[p.id]?.quantity || 0);
    const { factor, label, saleUnit } = purchaseFactor(p);
    // Presentaciones de COMPRA ordenadas por factor desc (caja → pack → unidad)
    const pres = (p.presentations || [])
      .filter(x => x.side === 'purchase')
      .map(x => ({ name: purchaseUnitLabel(x.name), factor: Number(x.factor) }))
      .filter(x => x.factor > 1)
      .sort((a, b) => b.factor - a.factor);

    if (pres.length === 0) {
      // Sin presentaciones: si hay conversión peso/volumen, mostrarla también
      if (factor > 1) {
        const whole = Math.floor(qty / factor);
        const rest = qty - whole * factor;
        if (rest === 0) return `${whole} ${label}`;
        return `${whole} ${label} y ${rest} ${saleUnit}`;
      }
      return `${qty} ${saleUnit}`;
    }

    let remaining = qty;
    const parts: string[] = [];
    for (const pr of pres) {
      const whole = Math.floor(remaining / pr.factor);
      if (whole > 0) parts.push(`${whole} ${pr.name}`);
      remaining = remaining - whole * pr.factor;
    }
    if (remaining > 0) parts.push(`${remaining} ${saleUnit}`);
    if (parts.length === 0) return `0 ${saleUnit}`;
    return parts.join(' y ');
  }

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-7xl mx-auto">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 mb-4">
        <h1 className="text-2xl sm:text-3xl font-bold">Productos</h1>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setCatModalOpen(true)}
            className="px-4 py-2 bg-teal-600 hover:bg-teal-700 text-white rounded-lg font-medium min-h-[44px] transition-colors"
          >
            📁 Categorías
          </button>
          <button
            onClick={() => setBulkOpen(!bulkOpen)}
            className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg font-medium min-h-[44px] transition-colors"
          >
            ⚡ Precios
          </button>
          <button
            onClick={() => { resetForm(); setShowForm(!showForm); }}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium min-h-[44px] transition-colors"
          >
            {showForm && !editingId ? 'Cancelar' : '+ Nuevo'}
          </button>
        </div>
      </div>

      {/* Error / Save messages */}
      {error && (
        <div className="bg-red-50 text-red-700 dark:text-red-300 p-4 rounded-lg mb-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <span className="text-sm font-medium">⚠️ {error}</span>
          <button
            onClick={loadData}
            className="px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700 transition-colors min-h-[44px]"
          >
            Reintentar
          </button>
        </div>
      )}
      {saveMessage && (
        <div className="mb-4 p-3 bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300 rounded text-center">
          {saveMessage}
        </div>
      )}

      {/* Search with autocomplete */}
      <div className="relative mb-4">
        <input
          ref={searchRef}
          placeholder="🔍 Buscar por nombre, SKU o código de barras..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="w-full px-4 py-2 mb-2 border rounded"
          aria-label="Buscar productos"
        />
        {loading && <span className="absolute right-3 top-1/2 -translate-y-1/2 animate-spin h-4 w-4 border-2 border-blue-500 border-t-transparent rounded-full"></span>}
        {showSuggestions && (
          <div className="absolute z-10 mt-1 w-full max-h-60 overflow-y-auto bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md shadow-lg">
            {suggestions.length === 0 ? (
              <div className="px-4 py-2 text-sm text-gray-500 dark:text-gray-400 dark:dark:text-gray-400">No se encontraron resultados</div>
            ) : (
              suggestions.slice(0, 8).map(p => (
                <div
                  key={p.id}
                  onMouseDown={() => {
                    setSearch(p.name || p.sku || '');
                    setShowSuggestions(false);
                  }}
                  className="px-4 py-2 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800"
                >
                  <div className="font-medium">{p.name}</div>
                  {p.sku && <div className="text-sm text-gray-500 dark:text-gray-400 dark:dark:text-gray-400">{p.sku}</div>}
                </div>
              ))
            )}
          </div>
        )}
      </div>

      {/* Filtros: inactivos + categorías */}
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <label className="flex items-center gap-2 text-sm font-medium cursor-pointer min-h-[44px]">
          <input
            type="checkbox"
            checked={showInactive}
            onChange={e => setShowInactive(e.target.checked)}
            className="h-5 w-5 rounded border-gray-300 dark:border-gray-600"
          />
          Mostrar inactivos
        </label>
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-500 dark:text-gray-400">Categoría:</span>
          <select
            value={categoryFilter}
            onChange={e => setCategoryFilter(e.target.value)}
            className="px-3 py-2 border rounded-lg text-sm min-h-[44px] bg-white dark:bg-gray-800"
          >
            <option value="all">Todos</option>
            {categories.map(c => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Form */}
      {showForm && (
        <form
          ref={formRef}
          onSubmit={handleSubmit}
          className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow mb-6"
        >
          <h2 className="text-lg font-bold mb-4">
            {editingId ? '✏️ Editar Producto' : '📦 Nuevo Producto'}
          </h2>

          {/* Stock actual — detallado y bien visible (solo en edición) */}
          {editingId && products.find(p => p.id === editingId) && (
            <div className="mb-4 p-4 rounded-lg border-2 border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-950/40">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <span className="text-sm font-semibold text-blue-800 dark:text-blue-200">📦 STOCK ACTUAL</span>
                <button
                  type="button"
                  onClick={() => openAdjustStock(editingId)}
                  className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-xs font-medium min-h-[44px] whitespace-nowrap"
                >
                  Ajustar stock
                </button>
              </div>
              <p className="text-2xl font-bold text-blue-900 dark:text-blue-100 mt-2">
                {stockBreakdown(products.find(p => p.id === editingId)!)}
              </p>
              <p className="text-xs text-blue-700 dark:text-blue-300 mt-1">
                Total: {Number(stockMap[editingId]?.quantity || 0)} {form.unit && form.unit !== 'unit' ? form.unit : 'u.'}
                {form.unit && form.unit !== 'unit' && ` · Unidad de venta: ${form.unit}`}
                {form.purchaseUnit && form.purchaseUnit !== 'unit' && ` · Compra: ${form.purchaseUnit}`}
              </p>
            </div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium mb-1">
                Nombre *
              </label>
              <input
                type="text"
                value={form.name}
                onChange={e => setForm({ ...form, name: e.target.value })}
                className="w-full px-3 py-2 border rounded"
                required
                autoFocus
              />
              {formErrors.name && (
                <p className="text-red-500 dark:text-red-400 text-sm mt-1">{formErrors.name}</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">
                Precio de venta *
              </label>
              <input
                type="number"
                step="0.01"
                value={form.salePrice}
                onChange={e => setForm({ ...form, salePrice: e.target.value })}
                className="w-full px-3 py-2 border rounded"
                placeholder="0.00"
                required
              />
              {formErrors.salePrice && (
                <p className="text-red-500 dark:text-red-400 text-sm mt-1">{formErrors.salePrice}</p>
              )}
            </div>
          </div>
          {/* Toggle advanced fields */}
          <div className="flex justify-between items-center mb-3">
            <button
              type="button"
              onClick={() => setShowAdvanced(!showAdvanced)}
              className="text-sm text-blue-600 dark:text-blue-400 hover:underline"
            >
              {showAdvanced ? '▼ Menos campos' : '▶ Más campos (SKU, código, costo, categoría, etc.)'}
            </button>
          </div>
          {showAdvanced && (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">SKU / Código</label>
                <input
                  type="text"
                  value={form.sku}
                  onChange={e => setForm({ ...form, sku: e.target.value })}
                  className="w-full px-3 py-2 border rounded"
                  placeholder="Ej: DET-001"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Código de barras</label>
                <input
                  type="text"
                  value={form.barcode}
                  onChange={e => setForm({ ...form, barcode: e.target.value })}
                  className="w-full px-3 py-2 border rounded"
                  placeholder="Escanear..."
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Costo</label>
                <input
                  type="number"
                  step="0.01"
                  value={form.costPrice}
                  onChange={e => setForm({ ...form, costPrice: e.target.value })}
                  className="w-full px-3 py-2 border rounded"
                  placeholder="0.00"
                />
                {formErrors.costPrice && (
                  <p className="text-red-500 dark:text-red-400 text-sm mt-1">{formErrors.costPrice}</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Unidad de Venta</label>
                <select
                  value={form.unit}
                  onChange={e => setForm({ ...form, unit: e.target.value })}
                  className="w-full px-3 py-2 border rounded"
                >
                  <option value="unit">Unidad</option>
                  <option value="1/2 docena">1/2 docena (6 u.)</option>
                  <option value="docena">Docena (12 u.)</option>
                  <option value="maple">Maple (30 u.)</option>
                  <option value="kg">Kg</option>
                  <option value="g">g</option>
                  <option value="lt">Litro</option>
                  <option value="ml">ml</option>
                  <option value="m">Metro</option>
                  <option value="pack">Pack</option>
                  <option value="box">Caja</option>
                </select>
                {form.unit === 'pack' && (
                  <div className="mt-2">
                    <label className="block text-xs text-gray-600 dark:text-gray-300 mb-1">Unidades por pack (factor)</label>
                    <input
                      type="number"
                      step="any"
                      min="0.0001"
                      placeholder="Ej: 12"
                      value={form.packSize}
                      onChange={e => setForm({ ...form, packSize: e.target.value })}
                      className="w-full px-3 py-2 border rounded"
                    />
                  </div>
                )}
                {form.unit === 'box' && (
                  <div className="mt-2">
                    <label className="block text-xs text-gray-600 dark:text-gray-300 mb-1">Unidades por caja (factor)</label>
                    <input
                      type="number"
                      step="any"
                      min="0.0001"
                      placeholder="Ej: 360"
                      value={form.cajaSize}
                      onChange={e => setForm({ ...form, cajaSize: e.target.value })}
                      className="w-full px-3 py-2 border rounded"
                    />
                  </div>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Unidad de compra</label>
                <select
                  value={form.purchaseUnit}
                  onChange={e => setForm({ ...form, purchaseUnit: e.target.value })}
                  className="w-full px-3 py-2 border rounded"
                >
                  <option value="unit">Unidad</option>
                  <option value="1/2 docena">1/2 docena (6 u.)</option>
                  <option value="docena">Docena (12 u.)</option>
                  <option value="maple">Maple (30 u.)</option>
                  <option value="kg">Kg</option>
                  <option value="lt">Litro</option>
                  <option value="pack">Pack</option>
                  <option value="box">Caja</option>
                </select>
                {form.purchaseUnit === 'pack' && (
                  <div className="mt-2">
                    <label className="block text-xs text-gray-600 dark:text-gray-300 mb-1">Unidades por pack (factor)</label>
                    <input
                      type="number"
                      step="any"
                      min="0.0001"
                      placeholder="Ej: 12"
                      value={form.packSize}
                      onChange={e => setForm({ ...form, packSize: e.target.value })}
                      className="w-full px-3 py-2 border rounded"
                    />
                  </div>
                )}
                {form.purchaseUnit === 'box' && (
                  <div className="mt-2">
                    <label className="block text-xs text-gray-600 dark:text-gray-300 mb-1">Unidades por caja (factor)</label>
                    <input
                      type="number"
                      step="any"
                      min="0.0001"
                      placeholder="Ej: 360"
                      value={form.cajaSize}
                      onChange={e => setForm({ ...form, cajaSize: e.target.value })}
                      className="w-full px-3 py-2 border rounded"
                    />
                  </div>
                )}
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  En qué unidad se carga la cantidad en la orden de compra (ej: maple = 30 u.).
                  Si el producto se vende en g/ml y compra en kg/litro, se convierte automáticamente.
                </p>
              </div>
              <div>
                <label className="flex items-center gap-2 text-sm font-medium cursor-pointer min-h-[44px]">
                  <input
                    type="checkbox"
                    checked={form.fractionalSale}
                    onChange={e => setForm({ ...form, fractionalSale: e.target.checked })}
                    className="h-4 w-4"
                  />
                  Venta fraccionada (precio por presentación)
                </label>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Habilitá esta opción para definir un precio propio por presentación
                  (1/2 docena, docena, maple). Sin esto, se usa un único precio de venta.
                </p>
              </div>
              {form.fractionalSale && (
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium mb-1">
                    🥚 Presentaciones (unidades de venta/compra con factor)
                  </label>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                    Para productos como huevos: definí cada presentación y cuántas unidades de la
                    unidad base implica (ej: 1/2 docena = 6, docena = 12, maple = 30). El precio
                    de venta de cada presentación es propio.
                  </p>
                  {form.presentations.map((pres, idx) => (
                  <div key={idx} className="flex flex-wrap items-center gap-2 mb-2">
                    <select
                      value={pres.side}
                      onChange={(e) => {
                        const arr = [...form.presentations];
                        arr[idx] = { ...arr[idx], side: e.target.value as 'sale' | 'purchase' };
                        setForm(f => ({ ...f, presentations: arr }));
                      }}
                      className="px-2 py-1.5 border rounded text-sm w-28"
                    >
                      <option value="sale">Venta</option>
                      <option value="purchase">Compra</option>
                    </select>
                    <input
                      type="text"
                      placeholder="Nombre (ej: docena)"
                      value={pres.name}
                      onChange={(e) => {
                        const arr = [...form.presentations];
                        arr[idx] = { ...arr[idx], name: e.target.value };
                        setForm(f => ({ ...f, presentations: arr }));
                      }}
                      className="px-2 py-1.5 border rounded text-sm flex-1 min-w-[120px]"
                    />
                    <input
                      type="number"
                      step="any"
                      min="0.0001"
                      placeholder="Factor"
                      title="Cuántas unidades de la base implica"
                      value={pres.factor}
                      onChange={(e) => {
                        const arr = [...form.presentations];
                        arr[idx] = { ...arr[idx], factor: e.target.value };
                        setForm(f => ({ ...f, presentations: arr }));
                      }}
                      className="px-2 py-1.5 border rounded text-sm w-24"
                    />
                    {pres.side === 'sale' ? (
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        placeholder="Precio"
                        value={pres.price}
                        onChange={(e) => {
                          const arr = [...form.presentations];
                          arr[idx] = { ...arr[idx], price: e.target.value };
                          setForm(f => ({ ...f, presentations: arr }));
                        }}
                        className="px-2 py-1.5 border rounded text-sm w-28"
                      />
                    ) : (
                      <span className="text-xs text-gray-400 w-28">sin precio</span>
                    )}
                    <button
                      type="button"
                      onClick={() => setForm(f => ({ ...f, presentations: f.presentations.filter((_, i) => i !== idx) }))}
                      className="px-2 py-1.5 bg-red-100 dark:bg-red-900/40 text-red-600 dark:text-red-300 rounded text-sm min-h-[36px]"
                    >
                      ✕
                    </button>
                  </div>
                ))}
                <button
                    type="button"
                    onClick={() => setForm(f => ({ ...f, presentations: [...f.presentations, { side: 'sale', name: '', factor: '1', price: '' }] }))}
                    className="px-3 py-1.5 bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 rounded text-sm font-medium min-h-[36px]"
                  >
                    + Agregar presentación
                  </button>
                </div>
              )}
              <div>
                <label className="block text-sm font-medium mb-1">Imagen</label>
                {imagePreview && (
                  <div className="mb-2 relative w-24 h-24">
                    <img src={imagePreview} alt="Vista previa" className="w-full h-full object-cover rounded-lg border" />
                    <button
                      type="button"
                      onClick={() => { setImageFile(null); setImagePreview(null); }}
                      className="absolute -top-2 -right-2 bg-red-600 text-white rounded-full w-6 h-6 text-xs"
                      style={{ minHeight: '24px' }}
                    >✕</button>
                  </div>
                )}
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f) { setImageFile(f); setImagePreview(URL.createObjectURL(f)); }
                  }}
                  className="w-full text-sm"
                />
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  PNG/JPG/WEBP. Se convierte a WebP optimizado al guardar.
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Categoría</label>
                <select
                  value={form.categoryId}
                  onChange={e => setForm({ ...form, categoryId: e.target.value })}
                  className="w-full px-3 py-2 border rounded"
                >
                  <option value="">Sin categoría</option>
                  {categories.map(c => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </select>
              </div>
              <div className="flex items-end">
                <label className="flex items-center gap-2 text-sm font-medium cursor-pointer min-h-[44px]">
                  <input
                    type="checkbox"
                    checked={form.isAvailableForSale}
                    onChange={e => setForm({ ...form, isAvailableForSale: e.target.checked })}
                    className="h-5 w-5 rounded border-gray-300 dark:border-gray-600"
                  />
                  Disponible para la venta
                </label>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Stock mínimo (alerta)</label>
                <input
                  type="number"
                  step="0.01"
                  value={form.minQuantity}
                  onChange={e => setForm({ ...form, minQuantity: e.target.value })}
                  className="w-full px-3 py-2 border rounded"
                  placeholder="0"
                />
              </div>
              <div className="md:col-span-1">
                <label className="block text-sm font-medium mb-1">Marca</label>
                <input
                  type="text"
                  value={form.brand}
                  onChange={e => setForm({ ...form, brand: e.target.value })}
                  className="w-full px-3 py-2 border rounded"
                  placeholder="Opcional"
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium mb-1">Descripción</label>
                <textarea
                  value={form.description}
                  onChange={e => setForm({ ...form, description: e.target.value })}
                  className="w-full px-3 py-2 border rounded"
                  placeholder="Opcional"
                  rows={3}
                />
              </div>
              <div className="md:col-span-3">
                <label className="block text-sm font-medium mb-1">
                  Proveedores (para órdenes de compra)
                </label>
                {suppliers.length === 0 ? (
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    No hay proveedores cargados. Creá proveedores en la sección Proveedores y luego asociá los productos.
                  </p>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-1">
                    {suppliers.map(s => {
                      const row = supplierPricing.find(r => r.supplierId === s.id);
                      return (
                        <div key={s.id} className="flex items-center gap-2 min-h-[44px]">
                          <label className="flex items-center gap-2 cursor-pointer flex-1">
                            <input
                              type="checkbox"
                              checked={!!row}
                              onChange={e => setSupplierPricing(rows =>
                                e.target.checked
                                  ? [...rows, { supplierId: s.id, costPrice: '', isDefault: false }]
                                  : rows.filter(r => r.supplierId !== s.id)
                              )}
                              className="h-5 w-5 rounded border-gray-300 dark:border-gray-600"
                            />
                            <span className="font-medium">{s.name}</span>
                          </label>
                          {row && (
                            <>
                              <input
                                type="number"
                                step="0.01"
                                min="0"
                                placeholder="Costo $"
                                value={row.costPrice}
                                onChange={e => setSupplierPricing(rows =>
                                  rows.map(r => r.supplierId === s.id ? { ...r, costPrice: e.target.value } : r)
                                )}
                                className="w-24 px-2 py-1 text-sm border rounded dark:bg-gray-800 dark:border-gray-600"
                              />
                              <label
                                className="flex items-center gap-1 cursor-pointer whitespace-nowrap"
                                title="Usar este costo como base para el precio a clientes"
                              >
                                <input
                                  type="radio"
                                  name="pricing-base"
                                  checked={row.isDefault}
                                  onChange={e => setSupplierPricing(rows =>
                                    rows.map(r => ({ ...r, isDefault: r.supplierId === s.id ? e.target.checked : false }))
                                  )}
                                  className="h-4 w-4"
                                />
                                <span className="text-xs text-gray-500 dark:text-gray-400">Base</span>
                              </label>
                            </>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          )}
          <div className="flex gap-3 mt-4">
            <button
              type="submit"
              disabled={submitting}
              className="px-6 py-2 bg-green-600 hover:bg-green-700 disabled:bg-green-600 text-white rounded-lg font-medium min-h-[44px] transition-colors disabled:opacity-70"
            >
              {submitting ? 'Guardando...' : editingId ? 'Guardar Cambios' : 'Crear Producto'}
            </button>
            <button
              type="button"
              onClick={() => { resetForm(); setShowForm(false); }}
              disabled={submitting}
              className="px-6 py-2 bg-gray-300 hover:bg-gray-400 disabled:bg-gray-300 text-gray-700 dark:text-gray-200 rounded-lg font-medium min-h-[44px] transition-colors disabled:opacity-70"
            >
              Cancelar
            </button>
          </div>
        </form>
      )}

      {/* Table / Mobile cards — compact with inline stock buttons */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
        {/* Mobile cards */}
        <div className="block sm:hidden">
          {products.length === 0 ? (
            <div className="px-4 py-8 text-center text-gray-500 dark:text-gray-400 dark:dark:text-gray-400">
              {search ? 'No se encontraron productos' : 'Hacé clic en "Nuevo Producto" para crear el primero.'}
            </div>
          ) : (
            products.map(p => {
              const stock = stockMap[p.id];
              const qty = Number(stock?.quantity || 0);
              const min = Number(stock?.minQuantity || 0);
              const low = qty <= min && min > 0;
              const stockPres = stockInPurchaseUnit(p);
              return (
                <div key={p.id} className={`p-4 border-b last:border-b-0 ${!p.isAvailableForSale ? 'opacity-50' : ''}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <button
                        onClick={() => startEdit(p)}
                        className="font-semibold text-gray-800 dark:text-gray-100 text-left hover:text-blue-600 dark:hover:text-blue-400 min-h-[44px]"
                      >{p.name}</button>
                      <p className="text-sm text-gray-500 dark:text-gray-400 dark:dark:text-gray-400">{p.sku || '-'}</p>
                    </div>
                    <span className="text-base font-bold text-green-600 dark:text-green-400">${Number(p.salePrice).toFixed(0)}</span>
                  </div>
                  <div className="flex items-center justify-between mt-3">
                    <div className="flex items-center gap-1">
                      <span className={`w-16 text-center font-bold ${low ? 'text-red-600' : 'text-gray-700 dark:text-gray-200'}`} title={low ? 'Bajo mínimo' : ''}>
                        {stockPres.value}<span className="text-[10px] text-gray-400"> {stockPres.unit}</span>
                      </span>
                      <button onClick={() => openAdjustStock(p.id)} className="w-8 h-8 min-h-[44px] min-w-[44px] flex items-center justify-center border rounded text-gray-500 dark:text-gray-400 dark:dark:text-gray-400 bg-white dark:bg-gray-800 text-xs">
                        ⋯
                      </button>
                    </div>
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => handleToggleActive(p)}
                        className={`px-3 py-1.5 rounded text-xs font-medium min-h-[44px] min-w-[44px] ${p.isAvailableForSale ? 'bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300' : 'bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300'}`}
                      >
                        {p.isAvailableForSale ? 'Activo' : 'Inact.'}
                      </button>
                      <button onClick={() => startEdit(p)} className="text-blue-600 dark:text-blue-400 hover:text-blue-800 text-sm font-medium px-3 py-1.5 min-h-[44px] min-w-[44px]">✏️</button>
                      {confirmDelete === p.id ? (
                        <>
                          <button onClick={() => handleDelete(p.id)} className="text-red-600 hover:text-red-800 text-sm font-medium px-3 py-1.5 min-h-[44px]">✕ Sí</button>
                          <button onClick={() => setConfirmDelete(null)} className="text-gray-500 dark:text-gray-400 dark:dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 text-sm font-medium px-3 py-1.5 min-h-[44px]">No</button>
                        </>
                      ) : (
                        <button onClick={() => setConfirmDelete(p.id)} className="text-red-600 hover:text-red-800 text-sm font-medium px-3 py-1.5 min-h-[44px] min-w-[44px]">🗑️</button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
        {/* Desktop table */}
        <table className="hidden sm:table w-full text-sm">
          <thead className="bg-gray-50 dark:bg-gray-700">
            <tr>
              <th className="px-3 py-2 text-left">Nombre</th>
              <th className="px-3 py-2 text-right">Precio</th>
              <th className="px-3 py-2 text-center">Stock</th>
              <th className="px-3 py-2 text-center">Estado</th>
              <th className="px-3 py-2 text-right">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {products.map(p => {
              const stock = stockMap[p.id];
              const qty = Number(stock?.quantity || 0);
              const min = Number(stock?.minQuantity || 0);
              const low = qty <= min && min > 0;
              const stockPres = stockInPurchaseUnit(p);
              return (
                <tr key={p.id} className={`border-t ${!p.isAvailableForSale ? 'opacity-50' : ''}`}>
                  <td className="px-3 py-2">
                  <div className="flex items-center gap-3">
                    {p.imagePath && (
                      <img
                        src={`${API}/product-image/${localStorage.getItem('tenantSlug') || ''}/${p.id}?t=${Date.now()}`}
                        alt={p.name}
                        className="w-10 h-10 object-cover rounded border border-gray-200 dark:border-gray-600 shrink-0"
                        onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }}
                      />
                    )}
                    <button
                      onClick={() => startEdit(p)}
                      className="font-medium text-left hover:text-blue-600 dark:hover:text-blue-400 min-h-[44px]"
                    >{p.name}</button>
                  </div>
                </td>
                  <td className="px-3 py-2 text-right font-bold text-green-600 dark:text-green-400">
                    ${Number(p.salePrice).toFixed(0)}
                  </td>
                  <td className="px-3 py-2 text-center">
                    <div className="flex items-center justify-center gap-1">
                      <span className={`w-16 text-center font-bold ${low ? 'text-red-600' : 'text-gray-700 dark:text-gray-200'}`} title={low ? 'Bajo mínimo' : ''}>
                        {stockPres.value}<span className="text-[10px] text-gray-400"> {stockPres.unit}</span>
                      </span>
                      <button onClick={() => openAdjustStock(p.id)} className="w-8 h-8 min-h-[44px] min-w-[44px] flex items-center justify-center border rounded text-gray-500 dark:text-gray-400 dark:dark:text-gray-400 bg-white dark:bg-gray-800 text-xs">
                        ⋯
                      </button>
                    </div>
                  </td>
                  <td className="px-3 py-2 text-center">
                    <button
                      onClick={() => handleToggleActive(p)}
                      className={`px-3 py-1.5 rounded text-xs font-medium min-h-[44px] min-w-[44px] ${p.isAvailableForSale ? 'bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300' : 'bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300'}`}
                    >
                      {p.isAvailableForSale ? 'Activo' : 'Inact.'}
                    </button>
                  </td>
                  <td className="px-3 py-2 text-right whitespace-nowrap">
                    <button onClick={() => startEdit(p)} className="text-blue-600 dark:text-blue-400 hover:text-blue-800 text-sm font-medium px-3 py-1.5 min-h-[44px] min-w-[44px]">✏️</button>
                    {confirmDelete === p.id ? (
                      <>
                        <button onClick={() => handleDelete(p.id)} className="text-red-600 hover:text-red-800 text-sm font-medium px-3 py-1.5 min-h-[44px]">✕ Sí</button>
                        <button onClick={() => setConfirmDelete(null)} className="text-gray-500 dark:text-gray-400 dark:dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 text-sm font-medium px-3 py-1.5 min-h-[44px]">No</button>
                      </>
                    ) : (
                      <button onClick={() => setConfirmDelete(p.id)} className="text-red-600 hover:text-red-800 text-sm font-medium px-3 py-1.5 min-h-[44px] min-w-[44px]">🗑️</button>
                    )}
                  </td>
                </tr>
              );
            })}
            {products.length === 0 && (
              <tr>
                <td colSpan={5} className="px-4 py-8 text-center text-gray-500 dark:text-gray-400 dark:dark:text-gray-400">
                  {search ? 'No se encontraron productos' : 'Hacé clic en "Nuevo Producto" para crear el primero.'}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Stock adjust modal */}
      {adjustStockId && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setAdjustStockId(null)}>
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-sm" onClick={e => e.stopPropagation()}>
            <div className="p-4 border-b">
              <h2 className="text-base font-bold text-gray-800 dark:text-gray-100">📦 Ajustar Stock</h2>
              {adjustStockId && products.find(p => p.id === adjustStockId) && (
                <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">
                  <span className="font-medium">{products.find(p => p.id === adjustStockId)?.name}</span>
                  {' — Stock actual: '}
                  <span className="font-semibold text-blue-600 dark:text-blue-400">
                    {stockBreakdown(products.find(p => p.id === adjustStockId)!)}
                  </span>
                </p>
              )}
            </div>
            <form onSubmit={handleStockAdjust} className="p-4 space-y-3">
              <div>
                <label className="block text-sm font-medium mb-1">Tipo</label>
                <select
                  value={stockForm.type}
                  onChange={e => setStockForm({ ...stockForm, type: e.target.value })}
                  className="w-full px-3 py-2 border rounded min-h-[44px]"
                >
                  <option value="in">Entrada (+)</option>
                  <option value="out">Salida (−)</option>
                  <option value="adjustment">Ajuste</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Cantidad *</label>
                <input
                  type="number"
                  step="0.01"
                  value={stockForm.quantity}
                  onChange={e => setStockForm({ ...stockForm, quantity: e.target.value })}
                  className="w-full px-3 py-2 border rounded min-h-[44px]"
                  placeholder="0"
                  required
                  autoFocus
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Motivo *</label>
                <input
                  type="text"
                  value={stockForm.reason}
                  onChange={e => setStockForm({ ...stockForm, reason: e.target.value })}
                  className="w-full px-3 py-2 border rounded min-h-[44px]"
                  placeholder="Ej: conteo físico, merma, devolución"
                  required
                />
              </div>
              <div className="flex gap-3 pt-2">
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium min-h-[44px] transition-colors"
                >
                  Confirmar
                </button>
                <button
                  type="button"
                  onClick={() => setAdjustStockId(null)}
                  className="flex-1 px-4 py-2 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 text-gray-700 dark:text-gray-200 rounded-lg font-medium min-h-[44px] transition-colors"
                >
                  Cancelar
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Summary */}
      {products.length > 0 && (
        <div className="mt-4 text-sm text-gray-500 dark:text-gray-400 dark:dark:text-gray-400">
          {products.length} producto{products.length !== 1 ? 's' : ''} •{' '}
          {products.filter(p => p.isAvailableForSale).length} activos •{' '}
          {products.filter(p => !p.isAvailableForSale).length} inactivos
          {products.filter(p => {
            const s = stockMap[p.id];
            return s && Number(s.quantity) <= Number(s.minQuantity || 0) && Number(s.minQuantity || 0) > 0;
          }).length} con stock bajo
        </div>
      )}

      {/* ⚡ Actualización masiva de precios */}
      {bulkOpen && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => !bulkBusy && setBulkOpen(false)}>
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-lg p-6 max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold">⚡ Actualización masiva de precios</h3>
              <button onClick={() => setBulkOpen(false)} className="text-gray-500 hover:text-gray-700 dark:text-gray-300 text-xl min-h-[44px] min-w-[44px]">✕</button>
            </div>

            <p className="text-sm font-semibold text-gray-700 dark:text-gray-200 mb-2">Tipo de ajuste</p>
            <div className="grid grid-cols-2 gap-2 mb-4">
              <button
                onClick={() => setBulkMode('percent')}
                className={`px-4 py-2.5 rounded-lg text-sm font-semibold border-2 min-h-[44px] transition-colors ${
                  bulkMode === 'percent'
                    ? 'bg-purple-600 border-purple-600 text-white'
                    : 'border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200'
                }`}
              >
                % Porcentaje
              </button>
              <button
                onClick={() => setBulkMode('amount')}
                className={`px-4 py-2.5 rounded-lg text-sm font-semibold border-2 min-h-[44px] transition-colors ${
                  bulkMode === 'amount'
                    ? 'bg-purple-600 border-purple-600 text-white'
                    : 'border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200'
                }`}
              >
                $ Monto fijo
              </button>
            </div>

            <p className="text-sm font-semibold text-gray-700 dark:text-gray-200 mb-1">
              Valor {bulkMode === 'percent' ? '(%)' : '($)'}
            </p>
            <input
              type="number"
              step="0.01"
              value={bulkValue}
              onChange={(e) => setBulkValue(e.target.value)}
              placeholder={bulkMode === 'percent' ? 'Ej: 10 sube 10% · -5 baja 5%' : 'Ej: 500 sube $500 · -200 baja $200'}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-base mb-4"
            />

            <p className="text-sm font-semibold text-gray-700 dark:text-gray-200 mb-2">Alcance</p>
            <div className="grid grid-cols-2 gap-2 mb-3">
              <button
                onClick={() => setBulkScope('all')}
                className={`px-4 py-2.5 rounded-lg text-sm font-semibold border-2 min-h-[44px] transition-colors ${
                  bulkScope === 'all'
                    ? 'bg-purple-600 border-purple-600 text-white'
                    : 'border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200'
                }`}
              >
                Todos los productos
              </button>
              <button
                onClick={() => setBulkScope('suppliers')}
                className={`px-4 py-2.5 rounded-lg text-sm font-semibold border-2 min-h-[44px] transition-colors ${
                  bulkScope === 'suppliers'
                    ? 'bg-purple-600 border-purple-600 text-white'
                    : 'border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200'
                }`}
              >
                Por proveedor
              </button>
            </div>

            {bulkScope === 'suppliers' && (
              <div className="mb-4 p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                  Seleccioná uno o varios proveedores (se actualizan sus productos en stock y a la venta):
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-1 max-h-40 overflow-y-auto">
                  {suppliers.map((s) => (
                    <label key={s.id} className="flex items-center gap-2 px-2 py-1.5 rounded text-sm cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600">
                      <input
                        type="checkbox"
                        checked={bulkSupplierIds.includes(s.id)}
                        onChange={(e) => setBulkSupplierIds((ids) => (e.target.checked ? [...ids, s.id] : ids.filter((i) => i !== s.id)))}
                        className="h-4 w-4"
                      />
                      {s.name}
                    </label>
                  ))}
                </div>
              </div>
            )}

            <div className="p-3 bg-purple-50 dark:bg-purple-900/20 rounded-lg mb-4 text-sm">
              <p className="font-semibold text-gray-800 dark:text-gray-100">
                Se actualizarán {bulkTargets.length} producto{bulkTargets.length !== 1 ? 's' : ''}
              </p>
              {bulkSample != null && bulkValueNum !== 0 && (
                <p className="text-gray-600 dark:text-gray-300 mt-1">
                  Ej: {fmt$(bulkSample)} → <b>{fmt$(Math.max(1, bulkSampleNew!))}</b>
                </p>
              )}
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Solo productos en stock y marcados como a la venta. El precio nunca baja de $1.</p>
            </div>

            <div className="flex gap-3 justify-end">
              <button onClick={() => setBulkOpen(false)} disabled={bulkBusy} className="px-4 py-2 rounded-lg text-sm font-semibold border border-gray-300 dark:border-gray-600 min-h-[44px]">
                Cancelar
              </button>
              <button onClick={applyBulk} disabled={bulkBusy || bulkTargets.length === 0} className="px-4 py-2 rounded-lg text-sm font-semibold bg-purple-600 text-white hover:bg-purple-700 disabled:opacity-60 min-h-[44px]">
                {bulkBusy ? 'Aplicando...' : `Aplicar a ${bulkTargets.length}`}
              </button>
            </div>
          </div>
        </div>
      )}
    {/* 📁 Gestión de categorías */}
      {catModalOpen && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => setCatModalOpen(false)}>
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-md p-6 max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold">📁 Categorías</h3>
              <button onClick={() => setCatModalOpen(false)} className="text-gray-500 hover:text-gray-700 dark:text-gray-300 text-xl min-h-[44px] min-w-[44px]">✕</button>
            </div>

            {catError && <div className="bg-red-50 text-red-700 dark:text-red-300 p-3 rounded-lg mb-3 text-sm">⚠️ {catError}</div>}

            {/* Create */}
            <div className="flex gap-2 mb-4">
              <input
                type="text" placeholder="Nueva categoría (ej: Bebidas, Limpieza...)"
                value={newCat}
                onChange={(e) => setNewCat(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && createCat()}
                className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-sm"
              />
              <button onClick={createCat} className="px-4 py-2 bg-teal-600 hover:bg-teal-700 text-white rounded-lg text-sm font-semibold min-h-[44px]">
                ＋ Crear
              </button>
            </div>

            {/* List */}
            <div className="space-y-2">
              {categories.length === 0 && (
                <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-4">Sin categorías todavía. Creá la primera arriba.</p>
              )}
              {categories.map((c) => {
                const count = products.filter((p) => p.categoryId === c.id).length;
                return (
                  <div key={c.id} className="flex items-center gap-2 bg-gray-50 dark:bg-gray-700 rounded-lg px-3 py-2">
                    {renamingId === c.id ? (
                      <>
                        <input
                          type="text" value={renameVal}
                          onChange={(e) => setRenameVal(e.target.value)}
                          onKeyDown={(e) => e.key === 'Enter' && renameCat(c.id)}
                          className="flex-1 px-2 py-1 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-sm"
                        />
                        <button onClick={() => renameCat(c.id)} className="px-2.5 py-1 bg-blue-600 text-white rounded text-xs min-h-[36px]">OK</button>
                        <button onClick={() => { setRenamingId(null); setRenameVal(''); }} className="px-2.5 py-1 bg-gray-200 dark:bg-gray-600 rounded text-xs min-h-[36px]">✕</button>
                      </>
                    ) : (
                      <>
                        <span className="flex-1 text-sm font-medium">{c.name}</span>
                        <span className="text-xs text-gray-500 dark:text-gray-400">{count} producto{count !== 1 ? 's' : ''}</span>
                        <button
                          onClick={() => { setRenamingId(c.id); setRenameVal(c.name); }}
                          className="px-2 py-1 text-xs rounded min-h-[36px] text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/30"
                          title="Renombrar"
                        >
                          ✏️
                        </button>
                        <button
                          onClick={() => deleteCat(c.id, c.name)}
                          className="px-2 py-1 text-xs rounded min-h-[36px] text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30"
                          title="Eliminar"
                        >
                          🗑
                        </button>
                      </>
                    )}
                  </div>
                );
              })}
            </div>

            <p className="text-xs text-gray-500 dark:text-gray-400 mt-4">
              💡 Asigná una categoría a cada producto desde el formulario de alta/edición (campos avanzados). La categoría con productos no se puede eliminar.
            </p>
          </div>
        </div>
      )}

      {/* Catálogo general: lo que buscaste y NO está en tu lista de productos */}
      {search.trim().length >= 2 && (catalogLoading || catalogHits.length > 0) && (
        <div className="mt-6 border-t border-gray-200 dark:border-gray-700 pt-4">
          <div className="flex items-baseline justify-between mb-1">
            <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-200">
              🔎 Catálogo general
              {catalogHits.length > 0 ? ` · ${catalogHits.length} sin agregar` : ''}
            </h3>
            {catalogVertical && (
              <span className="text-xs text-gray-500 dark:text-gray-400">{catalogVertical.name}</span>
            )}
          </div>
          <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
            Estos productos <span className="font-medium">no están en tu lista</span>. El catálogo es de solo
            lectura: si agregás uno se copia a tus productos y después lo editás como cualquier otro.
          </p>

          {catalogLoading && catalogHits.length === 0 && (
            <p className="text-sm text-gray-500 dark:text-gray-400">Buscando en el catálogo…</p>
          )}

          <ul className="divide-y divide-gray-200 dark:divide-gray-700 border border-gray-200 dark:border-gray-700 rounded-lg">
            {catalogHits.map((hit) => (
              <li key={hit.id} className="p-3 space-y-2">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-gray-800 dark:text-gray-100">{hit.name}</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                      <span className="uppercase tracking-wide text-[10px] bg-gray-100 dark:bg-gray-700 rounded px-1 py-0.5 mr-1">
                        {hit.matchType === 'barcode' ? 'código' : hit.matchType === 'sku' ? 'SKU' : 'nombre'}
                      </span>
                      {hit.gtin ? `EAN ${hit.gtin}` : 'sin código de barras'}
                      {hit.sku ? ` · SKU ${hit.sku}` : ''}
                      {hit.saleUnit ? ` · ${hit.saleUnit}` : ''}
                      {hit.categoryName ? ` · ${hit.categoryName}` : ''}
                      {hit.brand ? ` · ${hit.brand}` : ''}
                    </p>
                  </div>
                  <button
                    onClick={() => openCatalogAdd(hit)}
                    className="shrink-0 px-3 py-2 text-sm rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white font-medium min-h-[44px]"
                  >
                    {catalogAddId === hit.id ? 'Cancelar' : 'Agregar'}
                  </button>
                </div>

                {catalogAddId === hit.id && (
                  <div className="bg-gray-50 dark:bg-gray-700/40 rounded-lg p-3 space-y-3">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <label className="text-xs text-gray-600 dark:text-gray-300">
                        Nombre *
                        <input
                          value={catalogForm.name}
                          onChange={e => setCatalogForm({ ...catalogForm, name: e.target.value })}
                          className="mt-1 w-full px-2 py-2 border border-gray-300 dark:border-gray-600 rounded text-sm bg-white dark:bg-gray-800 min-h-[44px]"
                        />
                      </label>
                      <label className="text-xs text-gray-600 dark:text-gray-300">
                        SKU
                        <input
                          value={catalogForm.sku}
                          onChange={e => setCatalogForm({ ...catalogForm, sku: e.target.value })}
                          className="mt-1 w-full px-2 py-2 border border-gray-300 dark:border-gray-600 rounded text-sm bg-white dark:bg-gray-800 min-h-[44px]"
                        />
                      </label>
                      <label className="text-xs text-gray-600 dark:text-gray-300">
                        Categoría
                        <select
                          value={catalogForm.categoryId}
                          onChange={e => setCatalogForm({ ...catalogForm, categoryId: e.target.value })}
                          className="mt-1 w-full px-2 py-2 border border-gray-300 dark:border-gray-600 rounded text-sm bg-white dark:bg-gray-800 min-h-[44px]"
                        >
                          <option value="">— Sin categoría —</option>
                          {categories.map(c => (
                            <option key={c.id} value={c.id}>{c.name}</option>
                          ))}
                        </select>
                      </label>
                      <label className="text-xs text-gray-600 dark:text-gray-300">
                        Precio de venta *
                        <input
                          type="number" step="0.01" min="0.01" inputMode="decimal"
                          value={catalogForm.salePrice}
                          onChange={e => setCatalogForm({ ...catalogForm, salePrice: e.target.value })}
                          className="mt-1 w-full px-2 py-2 border border-gray-300 dark:border-gray-600 rounded text-sm bg-white dark:bg-gray-800 min-h-[44px]"
                        />
                      </label>
                      <label className="text-xs text-gray-600 dark:text-gray-300">
                        Precio de costo (opcional)
                        <input
                          type="number" step="0.01" min="0" inputMode="decimal"
                          value={catalogForm.costPrice}
                          onChange={e => setCatalogForm({ ...catalogForm, costPrice: e.target.value })}
                          className="mt-1 w-full px-2 py-2 border border-gray-300 dark:border-gray-600 rounded text-sm bg-white dark:bg-gray-800 min-h-[44px]"
                        />
                      </label>
                    </div>
                    <p className="text-[11px] text-gray-500 dark:text-gray-400">
                      El catálogo no trae precios ni las categorías de tu comercio: completá lo obligatorio (*)
                      y se agrega a tu lista. Después lo editás como cualquier producto.
                    </p>
                    <button
                      onClick={() => addFromCatalog(hit)}
                      disabled={
                        catalogBusy ||
                        !catalogForm.name.trim() ||
                        !catalogForm.salePrice ||
                        Number(catalogForm.salePrice) <= 0
                      }
                      className="w-full px-3 py-2 text-sm rounded-lg bg-green-600 hover:bg-green-700 disabled:opacity-50 text-white font-medium min-h-[44px]"
                    >
                      {catalogBusy ? 'Agregando…' : 'Agregar a mis productos'}
                    </button>
                  </div>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}